import React, { FC, useState, useEffect } from 'react';
import DefaultModal from 'components/Shared/DefaultModal';
import Button from 'components/UI/Button';
import { getConfig } from '../../../config/config';
import { useLanguage } from '../../../languages/languageContext';
import { IonIcon } from 'components/UI/IonIcon';
import * as api from 'api/documents';
import Loader from 'components/UI/Loader';

const { theme } = getConfig();

type FolderItem = {
  _id: string;
  title: string;
  creator: string;
  isPrivate?: boolean;
};

type Props = {
  showModal: boolean;
  onClose: () => void;
  documentId: string;
  currentFolderId?: string | null;
  onMoveSuccess: () => void;
};

export const MoveDocumentModal: FC<Props> = ({ showModal, onClose, documentId, onMoveSuccess }) => {
  const { downloads, btn } = useLanguage();
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<{
    id: string | null;
    title: string;
  }>({
    id: null,
    title: '',
  });
  const [navigationStack, setNavigationStack] = useState<Array<{ id: string | null; title: string }>>([]);

  useEffect(() => {
    if (showModal) {
      setCurrentPath({ id: null, title: downloads.root });
      setNavigationStack([]);
      setSelectedFolderId(null);
      loadFolders(null);
    }
  }, [showModal]);

  const loadFolders = async (folderId: string | null) => {
    setLoading(true);
    try {
      let response: any;
      if (folderId === null) {
        response = await api.getRootFolder('asc', 'name');
      } else {
        response = await api.getFolderById(folderId, 'asc', 'name');
      }
      setFolders(response?.folders || []);
    } catch (error) {
      console.error('Error loading folders:', error);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  const navigateToFolder = (folderId: string, title: string) => {
    setNavigationStack([...navigationStack, currentPath]);
    setCurrentPath({ id: folderId, title });
    setSelectedFolderId(folderId);
    loadFolders(folderId);
  };

  const goBack = () => {
    if (navigationStack.length > 0) {
      const previousPath = navigationStack[navigationStack.length - 1];
      setNavigationStack(navigationStack.slice(0, -1));
      setCurrentPath(previousPath);
      setSelectedFolderId(previousPath.id);
      loadFolders(previousPath.id);
    }
  };

  const handleMove = async () => {
    if (!documentId) return;

    setLoading(true);
    try {
      await api.moveDocument(documentId, selectedFolderId);
      onMoveSuccess();
      onClose();
    } catch (error) {
      console.error('Error moving document:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderFolderItem = (folder: FolderItem) => (
    <div
      key={folder._id}
      className="flex items-center justify-between px-4 py-3 cursor-pointer border-b transition-colors duration-200 hover:bg-gray-50"
      style={{ borderBottomColor: theme.BACKGROUND_SECONDARY }}
      onClick={() => navigateToFolder(folder._id, folder.title)}
    >
      <div className="flex items-center flex-1">
        <div className="relative mr-4">
          <IonIcon name="folder-outline" size={24} style={{ color: theme.TEXT_SECONDARY }} />
          {folder.isPrivate && (
            <div
              className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: theme.BACKGROUND_PRIMARY }}
            >
              <IonIcon name="lock-open-outline" size={12} style={{ color: theme.TEXT_SECONDARY }} />
            </div>
          )}
        </div>
        <span className="text-base flex-1" style={{ color: theme.TEXT_PRIMARY }}>
          {folder.title}
        </span>
      </div>
      <IonIcon name="chevron-forward" size={20} style={{ color: theme.TEXT_SECONDARY }} />
    </div>
  );

  return (
    <DefaultModal
      showModal={showModal}
      onClose={onClose}
      modalTitle={downloads.moveToFolder}
      modalWidth={500}
      leftContent={
        navigationStack.length > 0 ? (
          <div className="cursor-pointer" onClick={goBack}>
            <IonIcon name="arrow-back" size={24} style={{ color: theme.BUTTON_PRIMARY }} />
          </div>
        ) : null
      }
    >
      <div className="max-h-96 flex flex-col">
        <div
          className="pl-4 border-b"
          style={{
            borderBottomColor: theme.BACKGROUND_SECONDARY,
            backgroundColor: theme.BACKGROUND_SECONDARY,
          }}
        >
          <span className="text-sm" style={{ color: theme.TEXT_SECONDARY }}>
            {downloads.moveDocumentPath} <strong style={{ color: theme.TEXT_PRIMARY }}>{currentPath.title}</strong>
          </span>
        </div>

        <div className="max-h-75 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center">
              <Loader showLoader={true} />
            </div>
          ) : folders.length === 0 ? (
            <div className="flex justify-center items-center pt-2.5">
              <span className="text-base text-center" style={{ color: theme.TEXT_SECONDARY }}>
                {downloads.noFoldersMessage}
              </span>
            </div>
          ) : (
            folders.map((folder) => renderFolderItem(folder))
          )}
        </div>

        <div className="flex pt-5">
          <div className="flex-1">
            <Button label={btn.cancelBtn} background={theme.BUTTON_SECONDARY} onClick={onClose} />
          </div>
          <div className="flex-1">
            <Button label={downloads.move} onClick={handleMove} disabled={!loading} />
          </div>
        </div>
      </div>
    </DefaultModal>
  );
};
